import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShoppingBag, LayoutDashboard, LogIn, LogOut, Map as MapIcon, Camera, Home, Sparkles, Heart, ShieldAlert } from "lucide-react";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import HomePinDialog from "@/components/HomePinDialog";
import NotificationBell from "@/components/NotificationBell";
import { useSaves } from "@/context/SavesContext";

function GlobePinLogo({ size = 24 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M16 39.5 C 6 27 2 22 2 14 A 14 14 0 0 1 30 14 C 30 22 26 27 16 39.5 Z"
                  fill="currentColor" stroke="currentColor" strokeWidth="0.5" strokeLinejoin="round"/>
            <circle cx="16" cy="14" r="8.5" fill="#f7f3ea" stroke="#f7f3ea" strokeWidth="0.5"/>
            <g stroke="hsl(131 22% 29%)" strokeWidth="1.1" fill="none" strokeLinecap="round">
                <ellipse cx="16" cy="14" rx="8.5" ry="8.5"/>
                <ellipse cx="16" cy="14" rx="3.5" ry="8.5"/>
                <path d="M7.5 14 h17"/>
                <path d="M9 9.5 h14"/>
                <path d="M9 18.5 h14"/>
            </g>
            {/* Q4 coral centre-dot — the brand's distinctive quirk */}
            <circle cx="16" cy="14" r="3.5" fill="#e07856"/>
        </svg>
    );
}

export default function Nav() {
    const { user, logout } = useAuth();
    const { count } = useCart();
    const saves = useSaves();
    const navigate = useNavigate();
    const [pinOpen, setPinOpen] = useState(false);
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        if (!user) { setUnread(0); return; }
        let cancelled = false;
        (async () => {
            try { const { data } = await api.get("/my/feed"); if (!cancelled) setUnread(data.unread || 0); }
            catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [user]);

    const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

    return (
        <header className="sticky top-0 z-40 w-full safe-pt">
            <div className="glass border-b border-stone-200/60">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
                    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
                        <span className="text-primary transition-transform duration-200 group-hover:-translate-y-0.5">
                            <GlobePinLogo size={30} />
                        </span>
                        <div className="leading-none">
                            <div className="font-display text-xl font-black tracking-tight">From Here</div>
                            <div data-testid="nav-tagline" className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground mt-0.5 hidden xl:block">
                                Discover what&apos;s near you
                            </div>
                        </div>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        <Link to="/" data-testid="nav-map-link">
                            <Button variant="ghost" size="sm" className="rounded-full">
                                <MapIcon size={16} className="mr-2" /> Discover
                            </Button>
                        </Link>
                        {user && (
                            <>
                                <Link to="/feed" data-testid="nav-feed-link">
                                    <Button variant="ghost" size="sm" className="rounded-full relative">
                                        <Sparkles size={16} className="mr-2" /> Feed
                                        {unread > 0 && (
                                            <span data-testid="feed-badge" className="ml-1.5 min-w-4 h-4 px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">
                                                {unread}
                                            </span>
                                        )}
                                    </Button>
                                </Link>
                                <Link to="/dashboard" data-testid="nav-dashboard-link">
                                    <Button variant="ghost" size="sm" className="rounded-full">
                                        <LayoutDashboard size={16} className="mr-2" /> Dashboard
                                    </Button>
                                </Link>
                            </>
                        )}
                    </nav>

                    <div className="flex items-center gap-2">
                        {user?.role === "admin" && (
                            <Link to="/admin" data-testid="nav-admin-link" className="hidden sm:inline-flex" title="Admin console">
                                <Button variant="outline" size="sm" className="rounded-full border-red-500/60 bg-red-500/10 hover:bg-red-500/20 text-red-700">
                                    <ShieldAlert size={14} className="mr-1.5" /> Admin
                                </Button>
                            </Link>
                        )}
                        {user && (
                            <Link to="/saved" data-testid="nav-saved-link" className="hidden sm:inline-flex">
                                <Button variant="outline" size="sm" className="rounded-full border-stone-300 relative">
                                    <Heart size={16} className={saves?.count > 0 ? "fill-accent text-accent" : ""} />
                                    {saves?.count > 0 && (
                                        <span data-testid="saved-count" className="ml-2 min-w-5 h-5 px-1.5 rounded-full bg-accent text-accent-foreground text-xs font-bold flex items-center justify-center">
                                            {saves.count}
                                        </span>
                                    )}
                                </Button>
                            </Link>
                        )}
                        {user && <NotificationBell />}
                        <Link to="/cart" data-testid="nav-cart-link">
                            <Button variant="outline" size="sm" className="rounded-full relative border-stone-300">
                                <ShoppingBag size={16} />
                                {count > 0 && (
                                    <span data-testid="cart-count" className="ml-2 min-w-5 h-5 px-1.5 rounded-full bg-accent text-accent-foreground text-xs font-bold flex items-center justify-center">
                                        {count}
                                    </span>
                                )}
                            </Button>
                        </Link>
                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
                                    <button className="focus:outline-none">
                                        <Avatar className="w-9 h-9 border-2 border-stone-200">
                                            {user.picture && <AvatarImage src={user.picture} alt={user.name} />}
                                            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">{initials}</AvatarFallback>
                                        </Avatar>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel>
                                        <div className="text-sm font-semibold">{user.name}</div>
                                        <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => setPinOpen(true)} data-testid="menu-home-pin">
                                        <Home size={14} className="mr-2" /> {user.home_lat ? "Update home pin" : "Set home pin"}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate("/feed")} data-testid="menu-feed">
                                        <Sparkles size={14} className="mr-2" /> My feed
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate("/saved")} data-testid="menu-saved">
                                        <Heart size={14} className="mr-2" /> Saved {saves?.count > 0 ? `(${saves.count})` : ""}
                                    </DropdownMenuItem>
                                    {user.role === "admin" && (
                                        <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="text-red-700">
                                            <ShieldAlert size={14} className="mr-2" /> Admin console
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                                        <LayoutDashboard size={14} className="mr-2" /> Dashboard
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate("/dashboard/orders")} data-testid="menu-orders">
                                        <ShoppingBag size={14} className="mr-2" /> My Orders
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                                        <LogOut size={14} className="mr-2" /> Sign out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link to="/auth" data-testid="nav-signin-btn">
                                <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90 hover:text-white transition-transform hover:-translate-y-0.5">
                                    <LogIn size={16} className="mr-2" /> Sign in
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
            <HomePinDialog open={pinOpen} onOpenChange={setPinOpen} />
        </header>
    );
}
